import React, { createContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserRole, UserStatus } from '../constants/auth.constants';

export const AuthContext = createContext({
  token: null,
  loginState: false,
  user: null,
  permissions: null,
  isSuperAdmin: false,
  role: null,
  status: null,
  Login: (token, userData) => {},
  Logout: () => {}
});

export default function AuthContextProvider({ children }) {
  const navigate = useNavigate();
  const logoutTimer = useRef(null);

  // Function to retrieve auth data and check expiry
  const getStoredAuthData = () => {
    const stored = localStorage.getItem('authData');
    if (!stored) return {
      token: null,
      user: null,
      permissions: null,
      isSuperAdmin: false,
      role: null,
      status: null
    };

    try {
      const parsed = JSON.parse(stored);
      const now = new Date();
      if (now.getTime() > parsed.expiry) {
        localStorage.removeItem('authData');
        localStorage.removeItem('authToken'); // Remove old token if exists
        return {
          token: null,
          user: null,
          permissions: null,
          isSuperAdmin: false,
          role: null,
          status: null
        };
      }

      // Extract role and determine if superadmin
      const userRole = parsed.user?.role || parsed.role;
      const isSuperAdmin = userRole === UserRole.SUPERADMIN;

      return {
        token: parsed.token,
        user: parsed.user || null,
        permissions: parsed.permissions || null,
        isSuperAdmin,
        role: userRole,
        status: parsed.user?.status || parsed.status || null
      };
    } catch {
      localStorage.removeItem('authData');
      localStorage.removeItem('authToken');
      return {
        token: null,
        user: null,
        permissions: null,
        isSuperAdmin: false,
        role: null,
        status: null
      };
    }
  };

  const authData = getStoredAuthData();
  const [token, setToken] = useState(authData.token);
  const [loginState, setLoginState] = useState(!!authData.token);
  const [user, setUser] = useState(authData.user);
  const [permissions, setPermissions] = useState(authData.permissions);
  const [isSuperAdmin, setIsSuperAdmin] = useState(authData.isSuperAdmin);
  const [role, setRole] = useState(authData.role);
  const [status, setStatus] = useState(authData.status);

  useEffect(() => {
    setLoginState(!!token);

    // If token exists, check expiry and set auto logout timer
    const stored = localStorage.getItem('authData');
    if (stored) {
      const parsed = JSON.parse(stored);
      const now = new Date();
      const remainingTime = parsed.expiry - now.getTime();

      if (remainingTime > 0) {
        logoutTimer.current = setTimeout(() => {
          LogoutHandler();
        }, remainingTime);
      } else {
        LogoutHandler();
      }
    }

    return () => {
      if (logoutTimer.current) clearTimeout(logoutTimer.current);
    };
  }, [token]);

  const LoginHandler = (token, userData = {}) => {
    const now = new Date();
    const expiryTime = 1 * 24 * 60 * 60 * 1000;

    // Extract role from user data
    const userRole = userData.user?.role || userData.role;
    const userStatus = userData.user?.status || userData.status || UserStatus.ACTIVE;
    const isSuperAdmin = userRole === UserRole.SUPERADMIN;

    const item = {
      token,
      user: userData.user || null,
      permissions: userData.permissions || null,
      isSuperAdmin,
      role: userRole,
      status: userStatus,
      expiry: now.getTime() + expiryTime
    };

    localStorage.setItem('authData', JSON.stringify(item));
    // Keep backward compatibility
    localStorage.setItem('authToken', JSON.stringify({ token, expiry: item.expiry }));

    setToken(token);
    setUser(userData.user || null);
    setPermissions(userData.permissions || null);
    setIsSuperAdmin(isSuperAdmin);
    setRole(userRole);
    setStatus(userStatus);
    setLoginState(true);

    if (logoutTimer.current) clearTimeout(logoutTimer.current);
    logoutTimer.current = setTimeout(() => {
      LogoutHandler();
    }, expiryTime);
  };

  const LogoutHandler = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authData');
    localStorage.removeItem('username');
    localStorage.removeItem('email');
    localStorage.removeItem('id');
    setToken(null);
    setUser(null);
    setPermissions(null);
    setIsSuperAdmin(false);
    setRole(null);
    setStatus(null);
    setLoginState(false);
    navigate('/login');

    if (logoutTimer.current) {
      clearTimeout(logoutTimer.current);
      logoutTimer.current = null;
    }
  };

  const contextValue = {
    token,
    loginState,
    user,
    permissions,
    isSuperAdmin,
    role,
    status,
    Login: LoginHandler,
    Logout: LogoutHandler
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}
